import { Box, Paper, Button, TextField, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon, PlayArrow as PlayArrowIcon, Save as SaveIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import { useQueryBuilderData } from '../../../../context/QueryBuilderDataContext';
import type { QueryForm } from '../../../../hooks/QueryBuilder/useQueryForm';
import { QUERY_NAME_MAX_LENGTH } from '../../../../hooks/QueryBuilder/useSaveQuery';
import GroupSelector from './GroupSelector';

interface QueryToolbarProps {
    form: QueryForm;
    onReset: () => void;
}

function QueryToolbar({ form, onReset }: QueryToolbarProps) {
    const { t } = useLanguage();
    const { groups, loadingGroups, refetchGroups } = useQueryBuilderData();
    const { description, setDescription, isValid, counting, saving } = form;
    const hasDescription = description.trim().length > 0;

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 3, alignItems: 'center', mb: 2 }}>
                <TextField
                    size="small"
                    label={t('pages.clients.queryBuilder.queryNameRequired')}
                    name="queryDescription"
                    value={description}
                    onChange={event => setDescription(event.target.value)}
                    placeholder={t('pages.clients.queryBuilder.queryNamePlaceholder')}
                    inputProps={{ maxLength: QUERY_NAME_MAX_LENGTH }}
                    error={form.nameTaken}
                    helperText={form.nameTaken
                        ? t('pages.clients.queryBuilder.queryNameTaken')
                        : t('common.charCount', { count: description.length, max: QUERY_NAME_MAX_LENGTH })}
                    // Compteur hors du flux : il ne doit pas grandir le champ et désaligner la ligne
                    slotProps={{ formHelperText: { sx: { position: 'absolute', top: '100%' } } }}
                    sx={{ flexGrow: 1, flexBasis: '220px', minWidth: 0 }}
                />

                <Box sx={{ flexGrow: 1, flexBasis: '180px', minWidth: 0 }}>
                    <GroupSelector
                        selectedGroupId={form.selectedGroupId}
                        newGroupName={form.newGroupName}
                        availableGroups={groups}
                        loading={loadingGroups}
                        fullWidth
                        onGroupIdChange={form.selectGroup}
                        onCreateGroup={form.createGroup}
                        onRefetchGroups={refetchGroups}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ml: { xs: 0, lg: 'auto' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={onReset}
                        sx={{ textTransform: 'none', borderColor: 'text.secondary', color: 'text.secondary' }}
                    >
                        {t('pages.clients.queryBuilder.reset')}
                    </Button>
                    <Tooltip title={hasDescription ? '' : t('pages.clients.queryBuilder.provideQueryNameWarning')}>
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<PlayArrowIcon />}
                                onClick={form.execute}
                                disabled={counting || !isValid || !hasDescription}
                                sx={{ textTransform: 'none', bgcolor: 'primary.main' }}
                            >
                                {t(counting ? 'pages.clients.queryBuilder.executing' : 'pages.clients.queryBuilder.execute')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={form.save}
                        disabled={saving || !isValid || !hasDescription}
                        sx={{ textTransform: 'none', color: 'success.main', borderColor: 'success.main' }}
                    >
                        {t(saving ? 'pages.clients.queryBuilder.saving' : 'pages.clients.queryBuilder.save')}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

export default QueryToolbar;
